import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { ObjectUtils } from '../../utils/object.utils';
import { IEquipmentTypeFilter } from '../../models/equipment-filter.model';
import { ISystemDetails } from '../../models/common.models';
import { IEquipmentComponentFilter } from '../../models/equipment-component-filter.model';
import { IEquipmentValues } from '../../models/catalog.models';

@Component({
  selector: 'app-marked-filters',
  templateUrl: './marked-filters.component.html',
  styleUrls: ['./marked-filters.component.scss'],
  standalone: false,
})
export class MarkedFiltersComponent implements OnInit, OnChanges {
  activeFilters: { name: string; entities: string[] }[] = [];
  showBPRewoke: boolean = false;

  @Input() filters!: Array<any>;
  @Input() equipmentTypeFilter!: IEquipmentTypeFilter;
  @Input() equipmentComponentFilter!: IEquipmentComponentFilter;
  @Input() equipmentComponentFilterActive!: boolean;
  @Input() emptyCatalog!: boolean;
  @Input() systemDetails!: ISystemDetails;
  @Input() isEnhancedEquipmentTypeFilterEnabled: boolean = false;
  @Output() deselectFilterOption: EventEmitter<any> = new EventEmitter<any>();

  protected readonly router: Router = inject(Router);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters?.currentValue || changes.equipmentTypeFilter?.currentValue) {
      this.initFilters();
    }
    if (this.emptyCatalog && (this.systemDetails !== undefined)) {
      this.initFilters();
    }
    this.initFilters();
  }

  ngOnInit(): void {
    this.router.navigateByUrl(this.router.url, { replaceUrl: true }).then(() => {
      this.initFilters();
    });
  }

  initFilters(): void {
    const mergedActiveFilters: any[] = [];
    if (this.emptyCatalog) {
      const urlTree: UrlTree = this.router.parseUrl(this.router.url);
      if (urlTree.queryParams['fl-number']) {
        let flName: string = 'noName';
        let sysIvk: string = 'noIvk';
        if (this.systemDetails !== undefined) {
          flName = this.systemDetails.nameEnUs;
          sysIvk = this.systemDetails.siemensEquipmentId;
        }
        mergedActiveFilters.push({
          name: flName,
          entities: [sysIvk],
        });
      }
    }
    if (
      this.equipmentTypeFilter &&
      this.equipmentTypeFilter.isApplicable &&
      this.equipmentTypeFilter.activeValue?.length > 0 &&
      !this.equipmentComponentFilterActive
    ) {
      mergedActiveFilters.push({
        name: this.equipmentTypeFilter.name,
        entities: this.mapRelProductSysivkToValueArray(),
      });
    }
    // Add condition for equipmentComponentFilter
    if (this.equipmentComponentFilter &&
      this.equipmentComponentFilter.isApplicable
      && this.equipmentComponentFilterActive) {
      mergedActiveFilters.push({
        name: this.equipmentComponentFilter.name,
        entities: this.mapRelSparepartIvkToValueArray(),
      });
    }
    this.filters.forEach(filter => {
      if (filter.activeValue) {
        mergedActiveFilters.push({
          name: filter.name,
          entities: Array.isArray(filter.activeValue) ? filter.activeValue : [filter.activeValue],
        });
      }
    });
    this.activeFilters = mergedActiveFilters;
  }

  mapRelSparepartIvkToValueArray(): Array<string> {
    return this.equipmentComponentFilter.activeValue?.map(
      activeValue => this.equipmentComponentFilter?.values?.find(value => value['rel-sparepart-ivk'] === activeValue)?.value,
    ).filter((v): v is string => v !== undefined) ?? [];
  }

  mapValueToRelSparepartSysivk(option: string): string | string[] {
    return this.equipmentComponentFilter?.values?.find(value => value.value === option)?.['rel-sparepart-ivk'] || '';
  }

  mapRelProductSysivkToValueArray(): Array<string> {
    return this.equipmentTypeFilter.activeValue?.reduce((result: string[], activeValue: string): string[] => {
      const match: IEquipmentValues | undefined = this.equipmentTypeFilter.values?.find(
        (value: IEquipmentValues): boolean => value['rel-product-sysivk'] === activeValue,
      );
      if (match) {
        result.push(this.isEnhancedEquipmentTypeFilterEnabled ? `${match.value} (${match['rel-product-sysivk']})` : match.value);
      }
      return result;
    }, []) ?? [];
  }

  mapValueToRelProductSysivk(option: string): string {
    if (this.isEnhancedEquipmentTypeFilterEnabled) {
      return this.equipmentTypeFilter.values?.find(
        (value: IEquipmentValues): boolean => `${value.value} (${value['rel-product-sysivk']})` === option,
      )?.['rel-product-sysivk'] ?? '';
    }
    return this.equipmentTypeFilter.values?.find(
      (value: IEquipmentValues): boolean => value.value === option,
    )?.['rel-product-sysivk'] ?? '';
  }

  filterResults(filter: any, option: any, routeUrl: string): void {
    const pageUrl: string = decodeURIComponent(routeUrl.split('?')[0]);
    const urlTree: UrlTree = this.router.parseUrl(routeUrl);

    if (this.emptyCatalog && filter !== 'rel-sparepart-ivk') {
      delete urlTree.queryParams['rel-product-sysivk'];
      delete urlTree.queryParams['fl-number'];
      delete urlTree.queryParams['rel-sparepart-ivk'];

      urlTree.queryParams = ObjectUtils.shallowFilterObject(urlTree.queryParams, filter);
    }

    let queryParamsArray: any[] = [];
    //handle case where params are separated by comma for equipmentComponentFilter
    if (filter === 'rel-sparepart-ivk' && urlTree.queryParams[filter]) {
      queryParamsArray = urlTree.queryParams[filter].split(',');
    } else {
      queryParamsArray = urlTree.queryParams[filter];
    }
    if (Array.isArray(queryParamsArray)) {
      if (filter === 'rel-product-sysivk') {
        queryParamsArray = queryParamsArray.filter(queryParam => queryParam !== this.mapValueToRelProductSysivk(option));
      } else if (filter === 'rel-sparepart-ivk') {
        queryParamsArray = queryParamsArray.filter(queryParam => queryParam !== this.mapValueToRelSparepartSysivk(option));
      } else {
        queryParamsArray = queryParamsArray.filter(queryParam => queryParam !== option);
      }

      if (queryParamsArray.length === 0 && filter === 'rel-sparepart-ivk') {
        delete urlTree.queryParams[filter];
      } else {
        urlTree.queryParams[filter] = queryParamsArray.join(',');
      }
    } else {
      if (filter === 'rel-product-sysivk') {
        if (urlTree.queryParams[filter] === this.mapValueToRelProductSysivk(option)) {
          delete urlTree.queryParams[filter];
        }
      } else if (filter === 'rel-sparepart-ivk') {
        if (urlTree.queryParams[filter] === this.mapValueToRelSparepartSysivk(option)) {
          delete urlTree.queryParams[filter];
        }
      } else {
        if (urlTree.queryParams[filter] === option) {
          delete urlTree.queryParams[filter];
        }
      }
    }

    const url: string = this.router.createUrlTree(
      [pageUrl],
      {queryParams: {...urlTree.queryParams, page: '1'}},
    ).toString();
    this.router.navigateByUrl(url).then();
  }
}
